import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../molecules/PageHeader';
import { PayoutFormOffCanvas } from '../organisms/PayoutFormOffCanvas';
import { PayoutHistory } from '../organisms/PayoutHistory';
import { ExportOffCanvas } from '../organisms/ExportOffCanvas';
import { ImportCSVModal } from '../organisms/ImportCSVModal';
import { ImportBatchesOffCanvas } from '../organisms/ImportBatchesOffCanvas';
import { UnresolvedTokensBanner } from '../organisms/UnresolvedTokensBanner';
import { CryptoConflictBanner } from '../organisms/CryptoConflictBanner';
import { CryptoRemapTool } from '../organisms/CryptoRemapTool';
import { loadSettingsFromStorage } from '../../utils/storage';
import { usePayouts } from '../../utils/hooks/usePayouts';
import { usePayoutMutations } from '../../utils/hooks/usePayoutMutations';
import { generateUUID } from '../../utils/uuid';
import { useAuth } from '../../contexts/AuthContext';
import { fillMissingPayoutValues } from '../../utils/fillMissingPayoutValues';
import { Toast } from '../atoms/Toast';
import type { Payout } from '../../types/payout';
import type { GlobalSettings } from '../../types/settings';

type ToastState = { message: string; type: 'success' | 'error' } | null;

export const PayoutsPage: React.FC = () => {
  const payoutsQuery = usePayouts();
  const { addPayout, updatePayout, deletePayout } = usePayoutMutations();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingPayout, setEditingPayout] = useState<Payout | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isImportBatchesOpen, setIsImportBatchesOpen] = useState(false);
  const [isRemapOpen, setIsRemapOpen] = useState(false);
  const [isFillingMissingValues, setIsFillingMissingValues] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const payouts = payoutsQuery.data || [];

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      const storedSettings = await loadSettingsFromStorage();
      setSettings(storedSettings);
    };
    loadSettings();
  }, []);

  const handlePayoutSubmit = async (newPayout: Omit<Payout, 'id' | 'createdAt'>) => {
    setIsLoading(true);

    try {
      if (editingPayout) {
        const updatedPayout: Payout = {
          ...editingPayout,
          ...newPayout,
        };
        await updatePayout(updatedPayout);
        setEditingPayout(null);
      } else {
        const payout: Payout = {
          ...newPayout,
          id: generateUUID(),
          createdAt: new Date().toISOString(),
        };
        await addPayout(payout);
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPayout = (payout: Payout) => {
    setEditingPayout(payout);
    setIsFormOpen(true);
  };

  const handleDeletePayout = async (payoutId: string) => {
    await deletePayout(payoutId);
  };

  const handleCancelEdit = () => {
    setEditingPayout(null);
    setIsFormOpen(false);
  };

  // Corrige les tokens custom- en batch : PUT sur chaque payout modifié
  const handleFixCustomTokens = async (updatedPayouts: Payout[]) => {
    const originalById = new Map(payouts.map(p => [p.id, p]));
    const changed = updatedPayouts.filter(p => {
      const orig = originalById.get(p.id);
      if (!orig) return false;
      return (
        p.crypto?.id !== orig.crypto?.id ||
        p.sentCrypto?.id !== orig.sentCrypto?.id ||
        p.receivedCrypto?.id !== orig.receivedCrypto?.id
      );
    });
    await Promise.all(changed.map(p => updatePayout(p)));

    // Invalider le cache des prix pour les nouveaux coinIds — force un re-fetch
    // (staleTime: Infinity bloquerait sinon les null mis en cache avant la correction)
    const newCoinIds = new Set(
      changed.flatMap(p => [p.crypto?.id, p.sentCrypto?.id, p.receivedCrypto?.id].filter(Boolean))
    );
    for (const coinId of newCoinIds) {
      queryClient.invalidateQueries({ queryKey: ['crypto-price-at-date', coinId] });
      queryClient.invalidateQueries({ queryKey: ['crypto-price', coinId] });
    }
  };

  const handleFillMissingValues = async (monthPayouts: Payout[]) => {
    setIsFillingMissingValues(true);
    try {
      const { updated, failed } = await fillMissingPayoutValues(monthPayouts);
      await Promise.all(updated.map((p) => updatePayout(p)));
      if (failed.length > 0) {
        setToast({
          message: `${updated.length} valeur(s) complétée(s), ${failed.length} prix introuvable(s).`,
          type: failed.length === monthPayouts.length ? 'error' : 'success',
        });
      } else {
        setToast({ message: `${updated.length} valeur(s) complétée(s).`, type: 'success' });
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des valeurs manquantes:', error);
      setToast({ message: 'Erreur lors de la mise à jour des valeurs manquantes.', type: 'error' });
    } finally {
      setIsFillingMissingValues(false);
    }
  };

  return (
    <div className="container mx-auto px-4 pt-8 pb-8">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <ImportCSVModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        existingPayouts={payouts}
      />

      <div className="flex items-start justify-between mb-0">
        <PageHeader
          title="Payouts de cryptomonnaies"
          subtitle="Suivi des payouts de minage pour déclaration BNC"
        />
        <div className="flex items-center gap-2 mt-8">
          <button
            onClick={() => { setEditingPayout(null); setIsFormOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter
          </button>
          {isAuthenticated && (
            <button
              onClick={() => setIsImportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 11l3 3m0 0l3-3m-3 3V4" />
              </svg>
              Importer CSV
            </button>
          )}
          {isAuthenticated && (
            <button
              onClick={() => setIsImportBatchesOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Historique des imports
            </button>
          )}
          <button
            onClick={() => setIsRemapOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier un coinID
          </button>
        </div>
      </div>

      <UnresolvedTokensBanner payouts={payouts} onSave={handleFixCustomTokens} />
      <CryptoConflictBanner payouts={payouts} onSave={handleFixCustomTokens} />
      <CryptoRemapTool
        isOpen={isRemapOpen}
        onClose={() => setIsRemapOpen(false)}
        payouts={payouts}
        onSave={handleFixCustomTokens}
      />

      {isAuthenticated && (
        <ImportBatchesOffCanvas
          isOpen={isImportBatchesOpen}
          onClose={() => setIsImportBatchesOpen(false)}
        />
      )}

      <PayoutFormOffCanvas
        isOpen={isFormOpen}
        onClose={handleCancelEdit}
        onSubmit={handlePayoutSubmit}
        isLoading={isLoading}
        editingPayout={editingPayout}
        onCancelEdit={handleCancelEdit}
      />

      <PayoutHistory
        payouts={payouts}
        declarationFrequency={settings?.declarationFrequency || 'mensuel'}
        bncRate={settings?.bncRate}
        onEditPayout={handleEditPayout}
        onDeletePayout={handleDeletePayout}
        onFillMissingValues={handleFillMissingValues}
        isFillingMissingValues={isFillingMissingValues}
        setIsExportOpen={setIsExportOpen}
      />

      <ExportOffCanvas
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        payouts={payouts}
        declarationFrequency={settings?.declarationFrequency || 'mensuel'}
      />
    </div>
  );
};
